import { useLayoutEffect, useState } from 'react';

export type LoginStepMotionTokens = {
  blurPx: string;
  durationSec: number;
  exitDurationSec: number;
  shiftYpx: number;
  scale: number;
  ease: [number, number, number, number];
};

function readFloatVar(style: CSSStyleDeclaration, name: string): number {
  const v = parseFloat(style.getPropertyValue(name).trim());
  return Number.isFinite(v) ? v : 0;
}

function readLoginStepMotionTokens(): LoginStepMotionTokens {
  if (typeof document === 'undefined') {
    return {
      blurPx: '',
      durationSec: 0,
      exitDurationSec: 0,
      shiftYpx: 0,
      scale: 0.9,
      ease: [0, 0, 0, 0],
    };
  }

  const style = getComputedStyle(document.documentElement);
  const blur =
    style.getPropertyValue('--login-step-blur-radius').trim();
  const durRaw =
    style.getPropertyValue('--login-step-transition-duration').trim();
  const durNum = parseFloat(durRaw);
  const durationSec = durRaw.endsWith('ms')
    ? durNum / 1000
    : Number.isFinite(durNum)
      ? durNum
      : 0;
  const exitDurRaw =
    style.getPropertyValue('--login-step-exit-duration').trim();
  const exitDurNum = parseFloat(exitDurRaw);
  const exitDurationSec = exitDurRaw.endsWith('ms')
    ? exitDurNum / 1000
    : Number.isFinite(exitDurNum)
      ? exitDurNum
      : 0;
  const shiftRaw =
    style.getPropertyValue('--login-step-transition-shift-y').trim();
  const shiftYpx = parseFloat(shiftRaw);
  const scaleRaw = readFloatVar(style, '--login-step-scale');
  const scale =
    Number.isFinite(scaleRaw) && scaleRaw > 0 && scaleRaw <= 1
      ? scaleRaw
      : 0.9;

  const ease: [number, number, number, number] = [
    readFloatVar(style, '--login-step-ease-1'),
    readFloatVar(style, '--login-step-ease-2'),
    readFloatVar(style, '--login-step-ease-3'),
    readFloatVar(style, '--login-step-ease-4'),
  ];

  return {
    blurPx: blur,
    durationSec: Number.isFinite(durationSec) ? durationSec : 0,
    exitDurationSec: Number.isFinite(exitDurationSec) ? exitDurationSec : 0,
    shiftYpx: Number.isFinite(shiftYpx) ? shiftYpx : 0,
    scale,
    ease,
  };
}

export function useLoginStepMotionTokens(): LoginStepMotionTokens {
  const [tokens, setTokens] = useState<LoginStepMotionTokens>(() =>
    readLoginStepMotionTokens()
  );

  useLayoutEffect(() => {
    setTokens(readLoginStepMotionTokens());
  }, []);

  return tokens;
}
